import {
  ControlArea as SharedControlArea,
  LayoutSwitch,
  type LayoutSwitchOption,
} from '@/components/ControlArea';
import { TransactionsLayout } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/Detail/LeftContent/TransactionsDetail/hooks/useTransactionsLayout';
import { useTranslation } from '@/i18n';
import { toCommaSeparated } from '@/utils/convert/toCommaSeparated';
import { IconCard, IconTable } from '@moneyforward/ap-frontend-components';
import { FC, memo, useMemo } from 'react';

export type Props = {
  count: number;
  layout: TransactionsLayout;
  onLayoutChange: (layout: TransactionsLayout) => void;
};

export const ControlArea: FC<Props> = memo(
  ({ count, layout, onLayoutChange }) => {
    const { t } = useTranslation();

    // Ordered to match the design's card-then-table visual layout; the order
    // also decides arrow-key traversal in the shared `LayoutSwitch`.
    const options = useMemo<LayoutSwitchOption<TransactionsLayout>[]>(
      () => [
        {
          value: 'card',
          label: t('approving_invoice_reports.transactions.layout_card'),
          tooltip: t(
            'approving_invoice_reports.transactions.layout_card_tooltip'
          ),
          icon: <IconCard />,
          testId: 'transactions-layout-card',
        },
        {
          value: 'table',
          label: t('approving_invoice_reports.transactions.layout_table'),
          tooltip: t(
            'approving_invoice_reports.transactions.layout_table_tooltip'
          ),
          icon: <IconTable />,
          testId: 'transactions-layout-table',
        },
      ],
      [t]
    );

    return (
      <SharedControlArea
        testId='transactions-control-area'
        count={
          // `count` is reserved by i18next for pluralisation, so the
          // interpolation variable is `total`.
          t('approving_invoice_reports.transactions.count', {
            total: toCommaSeparated(count),
          })
        }
      >
        <LayoutSwitch
          layout={layout}
          onChange={onLayoutChange}
          options={options}
          ariaLabel={t('approving_invoice_reports.transactions.layout_label')}
        />
      </SharedControlArea>
    );
  }
);
ControlArea.displayName = 'ControlArea';
